import {useMemo} from 'react'
import {type AppBskyFeedDefs, AtUri} from '@atproto/api'
import {useLingui} from '@lingui/react/macro'

import {capsuleCoverFromEmbed} from '#/lib/capsuleCover'
import {type ClassroomContent} from '#/state/classrooms/types'
import {useCapsuleByUriQuery} from '#/state/queries/capsule'

/**
 * A capsule a classroom owns, in the shapes the app's capsule surfaces ask for.
 *
 * A capsule made for one classroom is a record and nothing else: no post was
 * written for it, which is exactly what keeps it off every profile and out of
 * every feed (see `PublishCapsuleDialog`). Every surface that draws a capsule in
 * this app draws it *through a post* — the feed's embed, the capsule card, the
 * thread — and all of them ask a post for the same few things: an address, an
 * author, a record that says what the work is, and an embed to play. A record in a
 * room has every one of them.
 *
 * So this is the one place that answers those questions, once, for both surfaces
 * the room draws: the grid's card and the Posts tab's embed. The post it builds is
 * never stored, never fetched and never moderated — it exists for the length of a
 * render, and it is the alternative to a second, hand-drawn capsule UI that drifts
 * away from the real one.
 */
export function useClassroomCapsule({
  entry,
  capsuleUri,
}: {
  entry: ClassroomContent
  capsuleUri: string
}): {
  /** The post the app's capsule surfaces believe they were handed. */
  postView: AppBskyFeedDefs.PostView
  /** The record, as the field bag the surfaces read (`title`, `level`…). */
  capsule: Record<string, unknown>
  /** The cover film, put back together from the record's own embed. */
  hasCover: boolean
  /** What to print where a capsule prints its name. */
  title: string
} {
  const {t: l} = useLingui()
  const {data: record} = useCapsuleByUriQuery(capsuleUri)

  const did = useMemo(() => new AtUri(capsuleUri).hostname, [capsuleUri])
  const cover = useMemo(
    () => capsuleCoverFromEmbed(record?.embed, did),
    [record?.embed, did],
  )

  /**
   * The title, the record first.
   *
   * The record is the truth and the entry is what the room can say before the read
   * comes back. `Untitled capsule` is the last resort because it is the studio's
   * own word for a capsule whose cover carries no title — a surface has to print
   * *something*, and an empty line reads as a title that failed to load.
   */
  const title =
    record?.title?.trim() ||
    entry.capsuleTitle?.trim() ||
    firstLine(entry.text) ||
    l`Untitled capsule`

  const postView = useMemo(
    () =>
      ({
        uri: capsuleUri,
        cid: '',
        indexedAt: entry.createdAt,
        author: {
          did: entry.authorDid,
          handle: entry.authorHandle,
          avatar: entry.authorAvatar,
        },
        record: {
          $type: 'app.bsky.feed.post',
          text: entry.text,
          createdAt: entry.createdAt,
          // The back-reference a publisher patches onto a real capsule's post, so
          // the surfaces recognise the work as a capsule (see `hasCapsuleEmbed`)
          // and read the count off it.
          capsule: {uri: capsuleUri, cid: ''},
          ...(typeof record?.slideCount === 'number'
            ? {slideCount: record.slideCount}
            : null),
        },
        ...(cover ? {embed: cover} : null),
        labels: [],
      }) as unknown as AppBskyFeedDefs.PostView,
    [capsuleUri, cover, entry, record?.slideCount],
  )

  const capsule = useMemo(
    () => ({...(record ?? {}), title}),
    [record, title],
  )

  return {postView, capsule, hasCover: !!cover, title}
}

/** The first line of a body of text, as a capsule's title. */
function firstLine(text: string): string {
  const [line] = text.trim().split('\n')
  return line?.trim() ?? ''
}
